import { Injectable, computed, inject } from '@angular/core'

import {
  Enums,
  ExpandedHuntPackageRecord,
  Environment,
  Tool,
  ContentRecord,
} from '@cyborg-monorepo/hunter/common'
import { BadgeVariant } from '@hyperkit'

import { gatherTags } from '../../../../helpers/utils'
import { getSeverityBadgeVariant } from '../../helpers/hunt-badge-variants'
import { NavigationService } from '../navigation.service'

import { HuntViewService } from './hunt-view.service'

@Injectable({ providedIn: 'root' })
export class PackageViewService {
  private readonly huntSvc = inject(HuntViewService)
  private readonly nav = inject(NavigationService)

  /** Current package ID from URL (or null) */
  readonly packageId = computed<string | null>(() => this.nav.packageId())

  /** All packages for the current hunt */
  readonly huntPackages = computed<ExpandedHuntPackageRecord[]>(() =>
    this.huntSvc.huntPackages(),
  )

  /** Active package based on packageId */
  readonly activePackage = computed<ExpandedHuntPackageRecord | null>(() => {
    const id = this.packageId()
    return id
      ? (this.huntPackages().find((pkg) => pkg.UUID === id) ?? null)
      : null
  })

  /** Shorthand for the package’s content array */
  readonly content = computed<
    | any[]
    | {
        uuids: string[]
        tools: string[]
        categories: string[]
        dependencies: string[]
        records: ContentRecord[]
      }
  >(() => this.activePackage()?.content ?? [])

  /** Query-logic table (if any) */
  readonly queryTable = computed<any[]>(
    () => this.activePackage()?.query_logic?.table ?? [],
  )

  // ——— Tab-content presence (drives which default-view tabs are shown) ———
  readonly hasQueryLogic = computed<boolean>(
    () => (this.activePackage()?.query_logic?.table?.length ?? 0) > 0,
  )
  readonly hasResearchData = computed<boolean>(() => {
    const pkg = this.activePackage()
    return (
      (pkg?.running_analyst_notes?.length ?? 0) > 0 ||
      !!pkg?.context?.threat_description?.trim()
    )
  })
  readonly hasReferencesData = computed<boolean>(() => {
    const r = this.activePackage()?.references
    return [r?.general, r?.analysis, r?.blog_links, r?.malware_samples].some(
      (a) => (a?.length ?? 0) > 0,
    )
  })
  readonly hasMitreData = computed<boolean>(() => {
    const m = this.activePackage()?.mapping?.mitre
    return (
      (m?.mitre_attack_tactics?.length ?? 0) > 0 ||
      (m?.mitre_attack_techniques?.length ?? 0) > 0 ||
      (m?.mitre_attack_technique_ids?.length ?? 0) > 0
    )
  })
  readonly hasResponseActionsData = computed<boolean>(() => {
    const ra = this.activePackage()?.response_actions
    return !!(
      ra?.analyst_runbook?.trim() ||
      ra?.mitigation_recommendations?.trim() ||
      ra?.playbook?.trim()
    )
  })

  /** UUID of the active package (or null) */
  readonly UUID = computed<string | null>(
    () => this.activePackage()?.UUID ?? null,
  )

  /** Whether package data is available */
  readonly isHydrated = computed<boolean>(() => !!this.activePackage())

  // ——— Metadata ———
  readonly title = computed<string>(
    () => this.activePackage()?.title ?? 'Untitled',
  )
  readonly author = computed<string>(
    () => this.activePackage()?.author ?? 'Unknown',
  )
  readonly isCustom = computed<boolean>(
    () => this.activePackage()?.source === Enums.SourceEnum.CUSTOMER,
  )
  readonly status = computed<string>(
    () => this.activePackage()?.status ?? 'Unknown',
  )
  readonly severity = computed<string>(
    () => this.activePackage()?.severity ?? 'N/A',
  )
  readonly severityVariant = computed<BadgeVariant>(() => {
    const sev = this.severity()
    return sev ? getSeverityBadgeVariant(sev) : 'neutral'
  })

  readonly huntCategory = computed<Enums.HuntCategoryEnum | undefined>(
    () => this.activePackage()?.hunt_category,
  )
  readonly description = computed<string>(
    () => this.activePackage()?.description ?? 'N/A',
  )
  readonly validation = computed<string>(
    () => this.activePackage()?.validation ?? 'N/A',
  )
  readonly validationTypes = computed<string[]>(
    () => this.activePackage()?.validation_types ?? [],
  )
  readonly mitigationRecommendations = computed<string>(
    () =>
      this.activePackage()?.response_actions?.mitigation_recommendations ??
      'N/A',
  )

  readonly dependencies = computed<string[]>(
    () => this.activePackage()?.dependencies ?? [],
  )

  // ——— Tag accessors ———
  readonly tags = computed(() => gatherTags(this.activePackage()))

  // ——— Analysis & Tools ———
  readonly environments = computed<Environment[]>(
    () => this.activePackage()?.analysis ?? [],
  )
  readonly toolsByEnvironment = computed<Tool[]>(() =>
    this.environments().flatMap((env) => env.tools ?? []),
  )
  readonly getAllEnvironments = computed<Environment[]>(
    () => this.activePackage()?.analysis ?? [],
  )

  readonly createdDate = computed<string>(() => {
    const pkg = this.activePackage()
    return pkg?.created_date
      ? new Date(pkg.created_date).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : ''
  })

  readonly getAllReportEnvironments = computed<Environment[]>(
    () => this.activePackage()?.reportAnalysis ?? [],
  )
  readonly getNumEnvironments = computed<number>(
    () => this.getAllEnvironments().length,
  )

  readonly selectedEnvironment = computed<Environment | null>(
    () =>
      this.getAllEnvironments().find(
        (env) => env.id === this.nav.environmentId(),
      ) ?? null,
  )

  readonly getToolsBySelectedEnvironment = computed<Tool[]>(
    () => this.selectedEnvironment()?.tools ?? [],
  )

  readonly getAllTools = computed<Tool[]>(() =>
    this.getAllEnvironments().flatMap((env) => env.tools ?? []),
  )

  /** Lookup a single tool by its ID */
  getTool(toolId: string) {
    return computed<Tool | null>(
      () => this.getAllTools().find((t) => t.id === toolId) ?? null,
    )
  }
}
